<template>
  <div class="bg-cover">
    <ContentContainer>
      <div class="grid grid-cols-1 md:grid-cols-2 py-12 md:py-40 gap-4">
        <div class="text-heading-h2 md:text-heading-h1 font-heading font-semibold max-w-[710px]">
          {{ $t('automatedControl.heading') }}
        </div>

        <div class="flex-col justify-center max-w-[710px]">
          <div class="font-body text-body-m">
            {{ $t('automatedControl.text') }}
          </div>

          <div class="mt-10 md:mt-15">
            <ButtonPrimary to="/about">
              {{ $t('automatedControl.aboutUs') }}
            </ButtonPrimary>
          </div>
        </div>
      </div>

      <!-- Slider tabs -->
      <div class="min-h-[65px] relative">
        <div class="bg-black w-full h-px absolute left-0 bottom-[3px]"></div>

        <div class="flex flex-row justify-between items-end overflow-x-scroll lg:overflow-x-hidden space-x-20 md:space-x-0"
             ref="sliderTabContainer"
        >
          <button v-for="product in products" :key="product.key"
                  class="group/product-tab flex flex-col justify-end space-y-3 items-center xl:min-w-[120px]"
                  :data-active="activeProductKey === product.key"
                  @click.prevent="selectProductTab(product.key)"
                  :ref="`sliderTabItemRef${product.key}`"
          >
            <div class="text-gray-500-tint text-heading-h4 font-heading font-semibold
                      hover:text-heading-h3
                      group-data-[active=true]/product-tab:text-heading-h3
                      group-data-[active=true]/product-tab:text-black
                      hover:transition-all
               ">
              {{ product.name }}
            </div>

            <div class="w-[7px] h-[7px] rounded-full bg-black"></div>
          </button>
        </div>
      </div>

      <!-- Slider -->
      <div class="mt-10 pb-16 md:pb-40">
        <div class="flex flex-row items-start overflow-x-scroll scrollbar-hidden snap-x snap-mandatory gap-x-6"
             ref="sliderContainer"
             @scroll="onSliderScroll"
             @scrollend="onSliderScrollEnd"
        >
          <div v-for="product in products" :key="product.key"
               @click.prevent="selectProductItem(product.key)"
               :data-active="activeProductKey === product.key"
               :data-product-key="product.key"
               class="min-w-[90vw] w-[90vw] md:min-w-[571px] md:w-[571px] group/slider-item cursor-pointer snap-normal snap-center"
               :ref="`sliderItemRef${product.key}`"
               :style="`--slideColor: ${product.color};`"
          >
            <div class="aspect-[284/240] md:aspect-auto w-full md:h-[428px] rounded-[8px] p-12 md:px-[120px] md:py-16 flex flex-col justify-center
                        bg-gray-400 group-hover/slider-item:bg-[var(--slideColor)]
                        group-data-[active=true]/slider-item:bg-[var(--slideColor)]"
            >
              <img :src="require(`@/assets/images/products/${product.key}.svg`)"
                   :alt="product.name"
                   class="w-full object-contain max-h-full group-hover/slider-item:scale-105 transition-all"
              />
            </div>

            <div class="px-2">
              <div class="mt-14 text-black text-heading-h4 font-heading font-semibold">
                {{ product.name }}
              </div>

              <div class="mt-6 text-black font-body text-body-m">
                {{ product.description }}
              </div>
            </div>
          </div>
        </div>

        <div class="mt-14 flex items-center space-x-6">
          <RoundArrowButton direction="left"
                            @click.prevent="goToPreviousSlide()"
                            :disabled="isFirstSlideActive()"
          />
          <RoundArrowButton @click.prevent="goToNextSlide()"
                            :disabled="isLastSlideActive()"
          />
        </div>
      </div>
    </ContentContainer>
  </div>
</template>

<script>
import ContentContainer from "@/components/ui/ContentContainer.vue";
import ButtonPrimary from "@/components/ui/ButtonPrimary.vue";
import RoundArrowButton from "@/components/ui/RoundArrowButton.vue";
import throttle from 'lodash/throttle';

export default {
  components: {
    RoundArrowButton,
    ContentContainer,
    ButtonPrimary,
  },

  data() {
    return {
      activeProductKey: 'cold',

      products: [
        {
          key: 'cold', // Used for interactions and image
          name: this.$t('automatedControl.productNames.cold'),
          description: this.$t('automatedControl.productDescriptions.cold'),
          color: '#46BBC2',
        },
        {
          key: 'boiler', // Used for interactions and image
          name: this.$t('automatedControl.productNames.boiler'),
          description: this.$t('automatedControl.productDescriptions.boiler'),
          color: '#9ACD32',
        },
        {
          key: 'waste', // Used for interactions and image
          name: this.$t('automatedControl.productNames.waste'),
          description: this.$t('automatedControl.productDescriptions.waste'),
          color: '#B358C9',
        },
        {
          key: 'heat', // Used for interactions and image
          name: this.$t('automatedControl.productNames.heat'),
          description: this.$t('automatedControl.productDescriptions.heat'),
          color: '#D95648',
        },
        {
          key: 'dry', // Used for interactions and image
          name: this.$t('automatedControl.productNames.dry'),
          description: this.$t('automatedControl.productDescriptions.dry'),
          color: '#E97451',
        },
        {
          key: 'bms', // Used for interactions and image
          name: this.$t('automatedControl.productNames.bms'),
          description: this.$t('automatedControl.productDescriptions.bms'),
          color: '#2BBD66',
        },
        {
          key: 'air', // Used for interactions and image
          name: this.$t('automatedControl.productNames.air'),
          description: this.$t('automatedControl.productDescriptions.air'),
          color: '#616BF2',
        },
        {
          key: 'pet', // Used for interactions and image
          name: this.$t('automatedControl.productNames.pet'),
          description: this.$t('automatedControl.productDescriptions.pet'),
          color: '#E0C05B',
        },
      ],
    }
  },

  methods: {
    selectProductTab(key) {
      this.activeProductKey = key;

      const refKey = `sliderItemRef${key}`;

      const slide = this.$refs[refKey][0];

      this.$refs.sliderContainer.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth',
      });
    },

    selectProductItem(key) {
      this.activeProductKey = key;

      if (window.innerWidth < 800) {
        // sliderTabContainer
        const refKey = `sliderTabItemRef${key}`;

        const tab = this.$refs[refKey][0];

        const offsetLeft = tab.offsetLeft - 50;

        this.$refs.sliderTabContainer.scrollTo({
          left: offsetLeft < 0 ? 0 : offsetLeft,
          behavior: 'smooth',
        });
      }
    },

    onSliderScroll: throttle(function () {
      if (window.innerWidth < 800) {
        this.selectSlideBasedOnScrollPosition();
      }
    }, 100),

    onSliderScrollEnd() {
      if (window.innerWidth < 800) {
        this.selectSlideBasedOnScrollPosition();
      }
    },

    selectSlideBasedOnScrollPosition() {
      const container = this.$refs.sliderContainer;
      const containerRect = container.getBoundingClientRect();

      const keys = [];
      const distances = [];

      for (const index in this.products) {
        const product = this.products[index];
        const productRef = this.$refs[`sliderItemRef${product.key}`][0];

        const tabRect = productRef.getBoundingClientRect();

        const relativeX = tabRect.left - containerRect.left;

        keys.push(product.key);
        distances.push(Math.abs(relativeX));
      }

      const minAbsoluteDistance = Math.min(...distances);
      const indexOfMin = distances.indexOf(minAbsoluteDistance);

      if (indexOfMin !== -1 && this.activeProductKey !== keys[indexOfMin]) {
        const productKey = keys[indexOfMin];

        this.selectProductItem(productKey);
      }
    },

    goToPreviousSlide() {
      if (this.isFirstSlideActive()) {
        return;
      }

      const currentActiveIndex = this.products.findIndex(product => product.key === this.activeProductKey);
      const previousProductKey = this.products.at(currentActiveIndex - 1).key;

      this.selectProductTab(previousProductKey);
    },

    goToNextSlide() {
      if (this.isLastSlideActive()) {
        return;
      }

      const currentActiveIndex = this.products.findIndex(product => product.key === this.activeProductKey);
      const nextProductKey = this.products.at(currentActiveIndex + 1).key;

      this.selectProductTab(nextProductKey);
    },

    isFirstSlideActive() {
      return this.activeProductKey === this.products[0].key;
    },

    isLastSlideActive() {
      return this.activeProductKey === this.products.at(-1).key;
    },
  },
}
</script>
